// SlateRotator — full-screen slate carousel with auto-advance, fade, and
// PREV / PAUSE-PLAY / NEXT controls. Keeps the current slate across data
// refreshes (matched by slate.key) and runs exactly one timer.
import React, { useEffect, useRef, useState } from 'react';
import { CaretLeft, CaretRight, Pause, Play } from '@phosphor-icons/react';

const FADE_MS = 400;

export default function SlateRotator({ slates, renderSlate, intervalMs = 13000, active = true, testidPrefix = 'slates' }) {
  const count = slates.length;
  const [shown, setShown] = useState(0);
  const [target, setTarget] = useState(0);
  const [visible, setVisible] = useState(true);
  const [paused, setPaused] = useState(false);
  const [navNonce, setNavNonce] = useState(0);
  const shownKey = useRef(slates[0]?.key);

  useEffect(() => { shownKey.current = slates[shown]?.key; }, [slates, shown]);

  // Data refresh: re-locate the current slate by key, never jump to slate 1.
  useEffect(() => {
    const idx = slates.findIndex((s) => s.key === shownKey.current);
    const safe = idx >= 0 ? idx : Math.min(shown, Math.max(0, count - 1));
    if (safe !== shown) { setShown(safe); setTarget(safe); }
    else if (target >= count) setTarget(safe);
  }, [slates, count]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fade out → swap → fade in.
  useEffect(() => {
    if (target === shown) return undefined;
    setVisible(false);
    const t = setTimeout(() => { setShown(target); setVisible(true); }, FADE_MS);
    return () => clearTimeout(t);
  }, [target, shown]);

  // The single rotation timer.
  useEffect(() => {
    if (paused || !active || count <= 1) return undefined;
    const id = setInterval(() => setTarget((t) => (t + 1) % count), intervalMs);
    return () => clearInterval(id);
  }, [paused, active, count, intervalMs, navNonce]);

  const go = (delta) => { setTarget((t) => (t + delta + count) % count); setNavNonce((n) => n + 1); };
  const slate = slates[Math.min(shown, count - 1)];
  if (!slate) return null;

  const btn = 'inline-flex items-center justify-center rounded-full border border-[#dcd4c5] bg-white text-[#013523] hover:bg-[#f5f0e6] transition-colors disabled:opacity-40';
  const btnStyle = { height: 'clamp(26px, 3.6vh, 38px)', minWidth: 'clamp(26px, 3.6vh, 38px)', padding: '0 clamp(8px, 0.7vw, 14px)', fontSize: 'clamp(10px, 0.8vw, 13px)' };

  return (
    <div className="h-full w-full flex flex-col min-h-0" data-testid={testidPrefix}>
      <div className="shrink-0 flex items-end justify-between gap-6" style={{ height: 'clamp(40px, 6.5vh, 66px)', paddingBottom: 'clamp(8px, 1.2vh, 14px)' }}>
        <div className="min-w-0 flex items-baseline gap-4">
          <div className="font-serif text-[#013523] truncate leading-none" style={{ fontSize: 'clamp(20px, 2.1vw, 34px)' }} data-testid={`${testidPrefix}-title`}>
            {slate.title}
          </div>
          {slate.subtitle && (
            <div className="uppercase tracking-[0.22em] text-[#c9a93a] font-semibold leading-none" style={{ fontSize: 'clamp(10px, 0.85vw, 13px)' }} data-testid={`${testidPrefix}-subtitle`}>
              {slate.subtitle}
            </div>
          )}
        </div>
        {count > 1 && (
          <div className="shrink-0 flex items-center gap-2" data-testid={`${testidPrefix}-controls`}>
            <div className="tabular-nums uppercase tracking-[0.2em] text-[#6a766e] mr-2" style={{ fontSize: 'clamp(10px, 0.85vw, 13px)' }} data-testid={`${testidPrefix}-pageinfo`}>
              {shown + 1} of {count}
            </div>
            <button type="button" className={btn} style={btnStyle} onClick={() => go(-1)} data-testid={`${testidPrefix}-prev`} aria-label="Previous">
              <CaretLeft weight="bold" /> <span className="ml-1 uppercase tracking-wider">Prev</span>
            </button>
            <button type="button" className={btn} style={btnStyle} onClick={() => setPaused((p) => !p)} data-testid={`${testidPrefix}-${paused ? 'play' : 'pause'}`} aria-label={paused ? 'Play' : 'Pause'}>
              {paused ? <Play weight="fill" /> : <Pause weight="fill" />}
              <span className="ml-1 uppercase tracking-wider">{paused ? 'Play' : 'Pause'}</span>
            </button>
            <button type="button" className={btn} style={btnStyle} onClick={() => go(1)} data-testid={`${testidPrefix}-next`} aria-label="Next">
              <span className="mr-1 uppercase tracking-wider">Next</span> <CaretRight weight="bold" />
            </button>
          </div>
        )}
      </div>
      <div
        className="flex-1 min-h-0"
        style={{ opacity: visible ? 1 : 0, transition: `opacity ${FADE_MS}ms ease` }}
        data-testid={`${testidPrefix}-slate`}
        data-slate-key={slate.key}
      >
        {renderSlate(slate)}
      </div>
    </div>
  );
}
